import { type PageAreaFlag, PAGE_AREA_FLAGS } from "./pageAreas";

/**
 * CIP fork: the lifecycle stage of every Studio feature area (README.md,
 * "Feature stages"). Stage is a communication label, reviewed in code; who
 * can see a feature is a PostHog flag and never derived from the stage.
 *
 * - `alpha`: early and incomplete; expect rough edges and changes.
 * - `beta`: working and in use, still being tested; details may change.
 * - `ga`: generally available; no badge.
 *
 * Keyed by page area (`pageAreas.ts`) so TypeScript refuses a new area
 * without a stage, and so the badge, the sidebar label and the Labs page all
 * resolve a route the same way the kill switch does.
 */
export const FEATURE_STAGES = ["alpha", "beta", "ga"] as const;
export type FeatureStage = (typeof FEATURE_STAGES)[number];

export type FeatureStageEntry = {
  /** What people see on the badge and the Labs page. */
  name: string;
  stage: FeatureStage;
  /** ISO date the current stage was set, for the Labs page. */
  since?: string;
  docsUrl?: string;
  /**
   * Opt-in gate: the PostHog flag of an Early Access Feature. When set, the
   * area only renders for people enrolled through the Labs page (and for
   * everyone while PostHog is not configured, so local development is never
   * gated). Leave unset for a feature that is visible to everyone.
   */
  earlyAccessFlag?: string;
};

export const FEATURE_STAGE_BY_AREA: Record<PageAreaFlag, FeatureStageEntry> = {
  "page-elicitations": { name: "Elicitations", stage: "ga" },
  "page-assemblies": {
    name: "Assemblies",
    stage: "beta",
    since: "2026-10-08",
  },
  "page-blueprints": {
    name: "Blueprints",
    stage: "beta",
    since: "2026-10-08",
  },
  "page-rubrics": { name: "Rubrics", stage: "beta", since: "2026-10-08" },
  "page-participants": { name: "Participants", stage: "ga" },
  "page-datasets": { name: "Datasets", stage: "ga" },
  "page-experiments": { name: "Experiments", stage: "ga" },
  "page-evals": { name: "Evaluators", stage: "ga" },
  "page-scores": { name: "Scores", stage: "ga" },
  "page-annotation-queues": { name: "Human Annotation", stage: "ga" },
  "page-tracing": { name: "Tracing", stage: "ga" },
  "page-prompts": { name: "Prompts", stage: "ga" },
  "page-playground": { name: "Playground", stage: "ga" },
  "page-dashboards": { name: "Dashboards", stage: "ga" },
  "page-models": { name: "Models", stage: "ga" },
  "page-alerts": { name: "Alerts", stage: "ga" },
  "page-automations": { name: "Automations", stage: "ga" },
  "page-settings": { name: "Settings", stage: "ga" },
  "page-setup": { name: "Setup", stage: "ga" },
  "page-system": { name: "System", stage: "ga" },
  "page-home": { name: "Home", stage: "ga" },
};

export const STAGE_LABEL: Record<Exclude<FeatureStage, "ga">, string> = {
  alpha: "Alpha",
  beta: "Beta",
};

export const STAGE_DESCRIPTION: Record<Exclude<FeatureStage, "ga">, string> = {
  alpha: "Early and incomplete. Expect rough edges and changes.",
  beta: "Working and in use, still being tested. Details may change.",
};

/** The stage entry for an area; `null` for a generally available one. */
export const stagedEntryFor = (
  area: PageAreaFlag | null,
): (FeatureStageEntry & { area: PageAreaFlag }) | null => {
  if (!area) return null;
  const entry = FEATURE_STAGE_BY_AREA[area];
  return entry.stage === "ga" ? null : { ...entry, area };
};

/** Every area that is not generally available, in sidebar order. */
export const stagedFeatures = (): Array<
  FeatureStageEntry & { area: PageAreaFlag }
> =>
  PAGE_AREA_FLAGS.flatMap((area) => {
    const entry = stagedEntryFor(area);
    return entry ? [entry] : [];
  });

/**
 * The stage name PostHog uses for an Early Access Feature, so a PostHog entry
 * can be checked against the registry.
 */
export const toPostHogStage = (
  stage: FeatureStage,
): "alpha" | "beta" | "general-availability" =>
  stage === "ga" ? "general-availability" : stage;
